import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Modal } from '@arco-design/web-react';
import styled from 'styled-components';

import { IconFont } from '@/components';
import { LargeSelect } from '@/components/LargeSelect';
import { VolumeOptionLabel } from '@/components/Workload/storage-settings';
import {
  MAAS_INPUT_PLACEHOLDER,
  MAAS_SELECT_PLACEHOLDER,
  maasRequiredRules,
  maasRequiredSelectRules,
} from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import { bytesToGi, parseStorageQuantityToGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/quantity';
import {
  APP_DATA_VOLUME_NAME,
  DATA_VOLUME_PVC_CAPACITY_FIELD,
  DATA_VOLUME_PVC_DISPLAY_NAME_FIELD,
  MODEL_DATA_VOLUME_NAME,
  VOLUME_SOURCE_PVC,
  getVolumeClaimName,
  getVolumeMountPath,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import { isReclaimPolicyLocked, resolveReclaimPolicyValue } from '@/delivery-type-plugin/components/reclaimPolicyField';
import { usePersistentVolumeClaims } from '@/delivery-type-plugin/hooks/usePersistentVolumeClaims';
import { useOptionalResourceFormContext } from '@/delivery-type-plugin/platform/resource-form';

enum Field {
  VOLUME_NAME = 'volumeName',
  SELECTED_PVC = 'selectedPvc',
  MOUNT_PATH = 'mountPath',
}

interface PvcOptionItem {
  value: string;
  label: string;
  storage?: string;
  description?: string;
}

interface MaasDataDiskSelectModalProps {
  open?: boolean;
  modelSizeBytes?: number;
  /** 是否按 modelSizeBytes 过滤容量不足的 PVC，默认 true；模型实例选已有盘传 false */
  filterByModelSize?: boolean;
  initVolume?: Record<string, unknown>;
  defaultMountPath?: string;
  volumeName?: string;
  /** 展示可编辑卷名称（应用实例）；与 volumeName 互斥 */
  showVolumeName?: boolean;
  /** 新建时默认卷名称 */
  defaultVolumeName?: string;
  hideMountPath?: boolean;
  /** 存储字段之后的扩展内容（如模型实例的 model.name / size / mountPath） */
  afterContent?: ReactNode;
  /** 保存前额外校验（如模型字段） */
  onValidateExtra?: () => Promise<void>;
  /** 弹窗标题，默认「添加已有数据盘」 */
  title?: string;
  /** SKU / 下单：锁定回收策略为 Delete */
  resourceEditMode?: string;
  onClose?: () => void;
  onSave?: (volume: Record<string, unknown>) => void;
}

function filterPvcsByModelSize<T extends { value: string; storage?: string }>(options: T[], modelSizeBytes?: number): T[] {
  if (!modelSizeBytes || modelSizeBytes <= 0) return options;
  const minGi = bytesToGi(modelSizeBytes);
  return options.filter((item) => {
    const pvcGi = parseStorageQuantityToGi(item.storage);
    return pvcGi !== undefined && pvcGi >= minGi;
  });
}

function renderPvcVolumeLabel(item: PvcOptionItem) {
  return (
    <VolumeOptionLabel
      iconfontName="icon-yunpan"
      value={item.value}
      label={item.label || item.value}
      extraItems={[
        item.storage ? { label: 'capacity', value: item.storage, frozenWidth: true } : null,
        item.description ? { label: 'desc', value: item.description, frozenWidth: 240 } : null,
      ].filter(Boolean)}
    />
  );
}

function resolveVolumeName(
  values: Record<string, unknown>,
  options: {
    showVolumeName?: boolean;
    volumeName?: string;
    defaultVolumeName?: string;
    initVolume?: Record<string, unknown>;
  },
) {
  if (options.showVolumeName) {
    const fromForm = String(values[Field.VOLUME_NAME] ?? '').trim();
    return fromForm || options.defaultVolumeName || options.initVolume?.name || APP_DATA_VOLUME_NAME;
  }
  return options.volumeName || options.initVolume?.name || MODEL_DATA_VOLUME_NAME;
}

function buildVolumeFromSelection(
  claimName: string,
  mountPath: string,
  values: Record<string, unknown>,
  options?: {
    initVolume?: Record<string, unknown>;
    volumeName?: string;
    showVolumeName?: boolean;
    defaultVolumeName?: string;
    pvcStorageGi?: number;
    pvcDisplayName?: string;
    lockReclaimPolicy?: boolean;
  },
) {
  const { initVolume, volumeName, showVolumeName, defaultVolumeName, pvcStorageGi, pvcDisplayName, lockReclaimPolicy } = options || {};
  return {
    name: resolveVolumeName(values, { showVolumeName, volumeName, defaultVolumeName, initVolume }),
    type: 'Data',
    reclaimPolicy: resolveReclaimPolicyValue(initVolume?.reclaimPolicy as string | undefined, lockReclaimPolicy),
    ...(pvcStorageGi !== undefined ? { [DATA_VOLUME_PVC_CAPACITY_FIELD]: pvcStorageGi } : {}),
    ...(pvcDisplayName ? { [DATA_VOLUME_PVC_DISPLAY_NAME_FIELD]: pvcDisplayName } : {}),
    source: {
      type: VOLUME_SOURCE_PVC,
      persistentVolumeClaimName: claimName,
      mountPath,
    },
  };
}

export function MaasDataDiskSelectModal({
  open,
  modelSizeBytes,
  filterByModelSize = true,
  initVolume,
  defaultMountPath = '/models',
  volumeName,
  showVolumeName,
  defaultVolumeName = APP_DATA_VOLUME_NAME,
  hideMountPath,
  afterContent,
  onValidateExtra,
  title,
  resourceEditMode: resourceEditModeProp,
  onClose,
  onSave,
}: MaasDataDiskSelectModalProps) {
  const { t } = useTranslation();
  const resourceFormCtx = useOptionalResourceFormContext();
  const resourceEditMode = resourceEditModeProp ?? resourceFormCtx?.resourceEditMode;
  const reclaimLocked = isReclaimPolicyLocked(resourceEditMode);
  const modalTitle = title ?? t('components:add.existing.data.disk');
  const [form] = Form.useForm();
  const { options, isLoading } = usePersistentVolumeClaims({
    resources_filter: undefined,
    status: 'available',
  });

  const filteredOptions = useMemo(
    () => (filterByModelSize ? filterPvcsByModelSize(options, modelSizeBytes) : options),
    [filterByModelSize, modelSizeBytes, options],
  );

  const fmtOptions = useMemo(
    () =>
      filteredOptions.map((item) => ({
        label: renderPvcVolumeLabel(item),
        value: item.value,
      })),
    [filteredOptions],
  );

  const [selectedPvc, setSelectedPvc] = useState<PvcOptionItem>();
  /** 标记当前弹窗会话是否已初始化，避免 PVC 列表刷新时 reset 掉用户已选值 */
  const openInitializedRef = useRef(false);

  // 仅弹窗打开时初始化一次，不与 PVC 列表刷新联动
  useEffect(() => {
    if (!open) {
      openInitializedRef.current = false;
      return;
    }
    if (openInitializedRef.current) return;
    openInitializedRef.current = true;

    form.resetFields();
    const claimName = getVolumeClaimName(initVolume);
    const mountPath = getVolumeMountPath(initVolume) || defaultMountPath;
    form.setFieldsValue({
      [Field.VOLUME_NAME]: initVolume?.name || defaultVolumeName,
      [Field.SELECTED_PVC]: claimName ?? '',
      [Field.MOUNT_PATH]: mountPath,
    });
    if (showVolumeName && !initVolume) {
      const currentName = form.getFieldValue(Field.VOLUME_NAME);
      if (!currentName) {
        form.setFieldValue(Field.VOLUME_NAME, defaultVolumeName);
      }
    }
    if (claimName) {
      const item = filteredOptions.find((o) => o.value === claimName);
      if (item) setSelectedPvc(item);
      return;
    }
    setSelectedPvc(undefined);
  }, [defaultMountPath, defaultVolumeName, filteredOptions, form, initVolume, open, showVolumeName]);

  // PVC 列表异步加载或刷新后，仅补齐 selectedPvc 展示，不重置表单
  useEffect(() => {
    if (!open) return;
    const claimName = form.getFieldValue(Field.SELECTED_PVC) as string | undefined;
    if (!claimName) return;
    const item = filteredOptions.find((o) => o.value === claimName);
    if (item) setSelectedPvc(item);
  }, [filteredOptions, form, open]);

  const handleSelectChange = useCallback(
    (value: string | undefined) => {
      const item = filteredOptions.find((o) => o.value === value);
      setSelectedPvc(item);
    },
    [filteredOptions],
  );

  const filterPvcOption = (inputValue: string, option: ReactElement) => {
    const value = option?.props?.value as string | undefined;
    const item = filteredOptions.find((pvc) => pvc.value === value);
    if (!item) return false;
    const search = inputValue.toLowerCase();
    return (
      item.value.toLowerCase().includes(search) ||
      (item.storage || '').toLowerCase().includes(search) ||
      (item.description || '').toLowerCase().includes(search)
    );
  };

  const handleCancel = useCallback(() => {
    form.resetFields();
    setSelectedPvc(undefined);
    onClose?.();
  }, [form, onClose]);

  const handleSave = useCallback(async () => {
    try {
      await form.validate();
      if (onValidateExtra) {
        await onValidateExtra();
      }
    } catch {
      return;
    }
    const values = form.getFieldsValue();
    const { selectedPvc: claimName, mountPath: formMountPath } = values;
    const mountPath = hideMountPath ? defaultMountPath : formMountPath;
    const pvcStorageGi = selectedPvc?.storage ? parseStorageQuantityToGi(selectedPvc.storage) : undefined;
    const pvcDisplayName = selectedPvc?.label?.trim() || undefined;
    onSave?.(
      buildVolumeFromSelection(claimName, mountPath, values, {
        initVolume,
        volumeName,
        showVolumeName,
        defaultVolumeName,
        pvcStorageGi,
        pvcDisplayName,
        lockReclaimPolicy: reclaimLocked,
      }),
    );
    handleCancel();
  }, [
    defaultMountPath,
    defaultVolumeName,
    form,
    handleCancel,
    hideMountPath,
    initVolume,
    onSave,
    onValidateExtra,
    reclaimLocked,
    selectedPvc,
    showVolumeName,
    volumeName,
  ]);

  const pvcSelectPlaceholder =
    !isLoading && filterByModelSize && modelSizeBytes && filteredOptions.length === 0
      ? t('data-center:maas.storage.pvc.insufficient')
      : MAAS_SELECT_PLACEHOLDER;

  const pvcSelectPlaceholderNode = useMemo(
    () => (
      <SelectPlaceholder>
        <IconFont name="icon-yunpan" style={{ fontSize: 36, opacity: 0.45 }} />
        <span>{pvcSelectPlaceholder}</span>
      </SelectPlaceholder>
    ),
    [pvcSelectPlaceholder],
  );

  return (
    <StyledModal title={modalTitle} visible={open} onCancel={handleCancel} onOk={handleSave} focusLock={false}>
      <Form form={form} layout="vertical">
        {showVolumeName ? (
          <Form.Item label={t('common:volume.name')} field={Field.VOLUME_NAME} rules={maasRequiredRules(t('common:volume.name'))}>
            <Input placeholder={MAAS_INPUT_PLACEHOLDER} />
          </Form.Item>
        ) : null}
        <Form.Item
          label={t('common:shared.storage.volume')}
          field={Field.SELECTED_PVC}
          rules={maasRequiredSelectRules(t('common:shared.storage.volume'))}
        >
          <LargeSelect
            showSearch
            loading={isLoading}
            options={fmtOptions}
            placeholder=""
            placeholderNode={pvcSelectPlaceholderNode}
            filterOption={filterPvcOption}
            onChange={handleSelectChange}
          />
        </Form.Item>
        {!hideMountPath ? (
          <Form.Item
            label={t('data-center:maas.model.mountPath')}
            field={Field.MOUNT_PATH}
            rules={maasRequiredRules(t('data-center:maas.model.mountPath'))}
          >
            <Input placeholder={MAAS_INPUT_PLACEHOLDER} />
          </Form.Item>
        ) : null}
      </Form>
      {afterContent}
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  width: 800px;
  .arco-modal-content {
    min-height: 360px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
  /* showSearch 模式下避免 prefix 占位被输入区挤压到不可见 */
  .arco-select-view-with-prefix .arco-select-prefix {
    flex-shrink: 0;
  }
`;

const SelectPlaceholder = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 52px;
  color: var(--color-text-3);
  white-space: nowrap;
`;
